import { Helmet } from 'react-helmet-async';
import Layout from '../components/layout/Layout';

export default function MobileAppPage() {
  const handleDownloadApp = () => {
    window.open('https://play.google.com/store/apps/details?id=com.metapic.app', '_blank');
  };

  return (
    <Layout>
      <Helmet>
        <title>Metapic Mobile App - All-in-One Microstock Tool for Stock Photographers | AI Metadata Generator & Multi-FTP Uploader</title>
        <meta name="description" content="The ultimate microstock app for photographers & creators. AI metadata generator, bulk image generator with FLUX & Recraft V3, image upscaler, background remover, multi-FTP upload to 10+ stock sites, model & property release manager. Download now on Android!" />
        <meta name="keywords" content="microstock app, stock photography app, AI metadata generator, batch FTP uploader, stock contributor tools, AI image generator mobile, image upscaler app, background remover, Adobe Stock uploader, Shutterstock FTP, Getty Images upload, model release app, property release manager, FLUX image generator, bulk stock photo upload, SEO keyword generator, microstock workflow, stock photography income, mobile FTP client, photographer tools" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://metapic.app/mobile-app" />
        <meta property="og:title" content="Metapic Mobile App - All-in-One Microstock Tool for Photographers" />
        <meta property="og:description" content="AI metadata generator, bulk image generator, multi-FTP upload to 10+ stock sites, release manager. The complete mobile stock photography workflow." />
        <meta property="og:image" content="https://metapic.app/og-mobile-app.png" />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://metapic.app/mobile-app" />
        <meta property="twitter:title" content="Metapic Mobile App - All-in-One Microstock Tool" />
        <meta property="twitter:description" content="AI metadata generator, bulk image generator, multi-FTP upload to 10+ stock sites. Complete stock photography workflow on mobile." />
        <meta property="twitter:image" content="https://metapic.app/og-mobile-app.png" />
        
        {/* Additional SEO */}
        <link rel="canonical" href="https://metapic.app/mobile-app" />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Metapic" />
        
        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MobileApplication",
            "name": "Metapic - Microstock Photography App",
            "operatingSystem": "Android",
            "applicationCategory": "Photography",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.8",
              "ratingCount": "1250"
            },
            "description": "All-in-one microstock app with AI metadata generator, bulk image generator, multi-FTP uploader to 10+ stock sites, release manager. The complete mobile workflow for stock photographers.",
            "features": "AI Metadata Generator, Bulk Image Generator, Image Upscaler, Background Remover, Multi-FTP Upload, Model Release Manager, Property Release Manager, Trending Keywords, Goal Setting"
          })}
        </script>
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "Metapic Mobile App",
            "applicationCategory": "BusinessApplication",
            "offers": {
              "@type": "AggregateOffer",
              "lowPrice": "0",
              "highPrice": "162",
              "priceCurrency": "USD",
              "offerCount": "3"
            },
            "operatingSystem": "Android",
            "description": "Professional microstock photography workflow app for creating, optimizing, and uploading stock content to multiple agencies."
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-20">
        {/* Hero Section */}
        <section className="max-w-7xl mx-auto px-4 py-16 md:py-24">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left: Text */}
            <div className="space-y-6">
              <div className="inline-block px-4 py-2 bg-indigo-100 text-indigo-700 rounded-full text-sm font-bold mb-4">
                The All-in-One Microstock App for Stock Photographers
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Your complete stock photography workflow
              </h1>
              <h2 className="text-xl md:text-2xl text-gray-600 leading-relaxed">
                right in your pocket.
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Metapic app helps microstock contributors create, optimize, and upload stock photos and videos faster. From AI-powered metadata and image generation to multi-agency FTP uploads and on-the-go model releases — everything you need is built into one powerful mobile app.
              </p>
              
              {/* Key Benefits */}
              <div className="space-y-3 pt-2">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <p className="text-gray-700">Upload stock photos and videos to multiple stock agencies simultaneously with multi-FTP support
</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <p className="text-gray-700">Generate SEO-optimized metadata images and video files</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <p className="text-gray-700">Create and sign on-screen model releases on-the-fly with your phone</p>
                </div>
              </div>
              
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <button 
                  onClick={handleDownloadApp}
                  className="inline-flex items-center gap-3 px-6 py-3 bg-black hover:bg-gray-900 text-white rounded-lg font-medium transition-colors shadow-lg hover:shadow-xl"
                >
                  <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                  </svg>
                  <div className="text-left">
                    <div className="text-xs">GET IT ON</div>
                    <div className="text-base font-semibold -mt-1">Google Play</div>
                  </div>
                </button>
              </div>
            </div>

            {/* Right: App Screenshot */}
            <div className="relative">
              <img 
                src="/images/mobile-app/metapic-app-hero.png" 
                alt="Metapic Mobile App Screenshot" 
                className="max-w-sm mx-auto shadow-2xl"
              />
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-purple-200 rounded-full opacity-50 blur-2xl"></div>
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-indigo-200 rounded-full opacity-50 blur-2xl"></div>
            </div>
          </div>
        </section>

        {/* Why Metapic Section */}
        <section className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Why Metapic?
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Built specifically for microstock contributors who need speed, efficiency, and professional results
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-indigo-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Trends & Daily Insights</h3>
                <p className="text-gray-600 text-sm">Stay ahead with daily Shutterstock trends and AI-powered content inspiration</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Upload Everywhere</h3>
                <p className="text-gray-600 text-sm">One upload to 10+ agencies including Adobe, Shutterstock, Getty</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Beginner Friendly</h3>
                <p className="text-gray-600 text-sm">No steep learning curve, start earning from day one</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-amber-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Mobile First</h3>
                <p className="text-gray-600 text-sm">Work offline, sync later. Create anywhere, anytime</p>
              </div>
            </div>
          </div>
        </section>

        {/* Core Feature: AI Metadata Generator */}
        <section className="bg-gradient-to-b from-indigo-50 to-white py-20">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <div className="inline-block px-4 py-2 bg-indigo-600 text-white rounded-full text-sm font-bold mb-4">
                Core Feature
              </div>
              <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
                AI Metadata Generator
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Generate SEO-ready titles, descriptions, and up to 50 keywords for your photos and videos
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
              <div className="space-y-6">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                      <svg className="w-4 h-4 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Batch Processing</h4>
                      <p className="text-gray-600">Generate metadata for up to 100 images at once</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                      <svg className="w-4 h-4 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Beginner Friendly</h4>
                      <p className="text-gray-600">Intuitive interface with easy refinement tools</p>
                    </div>
                  </div>
                  

                  
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                      <svg className="w-4 h-4 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">SEO Optimized</h4>
                      <p className="text-gray-600">Keywords crafted for maximum discoverability</p>
                    </div>
                  </div>
                </div>

                <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-6">
                  <h4 className="font-bold text-indigo-900 mb-2">Perfect For</h4>
                  <p className="text-indigo-700 text-sm">
                    Photographers uploading to Adobe Stock, Shutterstock, Freepik, Getty Images, and other microstock platforms that require optimized metadata.
                  </p>
                </div>
              </div>
              
              <div className="rounded-2xl aspect-[9/16] max-w-sm mx-auto shadow-2xl overflow-hidden bg-black">
                <iframe
                  className="w-full h-full"
                  src="https://www.youtube.com/embed/HMwwbrzuu1Y"
                  title="AI Metadata Generator Demo"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          </div>
        </section>

        {/* Release Manager Section */}
        <section className="bg-gradient-to-b from-blue-50 to-white py-20">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 bg-blue-600 text-white rounded-full text-sm font-bold mb-4">
                Release Manager
              </div>
              <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
                 Model & Property Releases
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
                Professional solution for photographers and studios managing model and property releases across multiple agencies right from your phone!
              </p>
              
              {/* Killer Feature Highlight */}
              <div className="bg-white rounded-2xl p-8 max-w-4xl mx-auto shadow-lg border border-gray-200">
                <div>
                  <div className="text-2xl font-bold text-gray-900 mb-3">One Tap. Multiple Agencies.</div>
                  <div className="text-lg text-gray-600 mb-6">Client signs once → Generate 10+ agency-specific releases instantly</div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                    <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                      <div className="text-3xl font-bold text-gray-900 mb-1">1×</div>
                      <div className="text-sm text-gray-600">Single Signature</div>
                    </div>
                    <div className="flex items-center justify-center">
                      <svg className="w-8 h-8 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                      <div className="text-3xl font-bold text-gray-900 mb-1">10+</div>
                      <div className="text-sm text-gray-600">Agency Releases</div>
                    </div>
                  </div>
                  <div className="mt-4 text-sm text-gray-500">
                    Adobe Stock • Shutterstock • Getty Images • iStock • Dreamstime • 123RF • Depositphotos & more
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="text-xs text-gray-500 text-center">
                      Free to Use for membership with minimum monthly membership @ $10 / month
                    </div>
                    <div className="text-xs font-semibold text-gray-700 text-center mt-2">
                      Create as many Release as you want!!
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-12 items-start max-w-6xl mx-auto mb-12">
              <div className="space-y-6 order-2 md:order-1">
                {/* Manual Image Slider with Navigation - 7 Images */}
                <div className="rounded-2xl aspect-[9/21] max-w-sm mx-auto shadow-2xl overflow-hidden bg-black relative group">
                  <div className="relative w-full h-full">
                    <img 
                      src="/images/slider-release/1.My-model-release-collections.jpeg" 
                      alt="Release Manager Screenshots" 
                      className="w-full h-full object-contain transition-opacity duration-300" 
                      id="slider-img-release"
                      data-current-index="0"
                    />
                  </div>
                  
                  {/* Navigation Buttons */}
                  <button 
                    onClick={() => {
                      const img = document.getElementById('slider-img-release') as HTMLImageElement;
                      const indicator = document.getElementById('slider-indicator-release');
                      const images = [
                        '/images/slider-release/1.My-model-release-collections.jpeg',
                        '/images/slider-release/2.On-screen-sign-release.jpeg',
                        '/images/slider-release/3.PDF-model_release-preview.jpeg',
                        '/images/slider-release/4.create-model-release-phone-1.jpeg',
                        '/images/slider-release/5.create-model-release-2.jpeg',
                        '/images/slider-release/6.create-property-owner-data.jpeg',
                        '/images/slider-release/7.photographer-sign-release.jpeg'
                      ];
                      if (!img || !indicator) return;
                      let current = parseInt(img.getAttribute('data-current-index') || '0');
                      current = (current - 1 + images.length) % images.length;
                      img.style.opacity = '0';
                      setTimeout(() => {
                        img.src = images[current];
                        img.setAttribute('data-current-index', current.toString());
                        indicator.textContent = `${current + 1} / 7`;
                        img.style.opacity = '1';
                      }, 150);
                    }}
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/80 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  
                  <button 
                    onClick={() => {
                      const img = document.getElementById('slider-img-release') as HTMLImageElement;
                      const indicator = document.getElementById('slider-indicator-release');
                      const images = [
                        '/images/slider-release/1.My-model-release-collections.jpeg',
                        '/images/slider-release/2.On-screen-sign-release.jpeg',
                        '/images/slider-release/3.PDF-model_release-preview.jpeg',
                        '/images/slider-release/4.create-model-release-phone-1.jpeg',
                        '/images/slider-release/5.create-model-release-2.jpeg',
                        '/images/slider-release/6.create-property-owner-data.jpeg',
                        '/images/slider-release/7.photographer-sign-release.jpeg'
                      ];
                      if (!img || !indicator) return;
                      let current = parseInt(img.getAttribute('data-current-index') || '0');
                      current = (current + 1) % images.length;
                      img.style.opacity = '0';
                      setTimeout(() => {
                        img.src = images[current];
                        img.setAttribute('data-current-index', current.toString());
                        indicator.textContent = `${current + 1} / 7`;
                        img.style.opacity = '1';
                      }, 150);
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/80 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                  
                  {/* Slide Counter */}
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/70 text-white px-4 py-2 rounded-full text-sm font-semibold z-10" id="slider-indicator-release">
                    1 / 7
                  </div>
                </div>
              </div>
              
              <div className="space-y-6 order-1 md:order-2">
                <h3 className="text-2xl font-bold text-gray-900">Batch Release Creation — The Game Changer</h3>
                
                {/* Time Savings Comparison - Side by Side */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-white rounded-lg p-3 border border-gray-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-5 h-5 bg-red-500 rounded-md flex items-center justify-center flex-shrink-0">
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <h4 className="font-bold text-gray-900 text-xs">Manual Process</h4>
                    </div>
                    <p className="text-gray-900 text-xl font-bold mb-1">2.5 hours</p>
                    <p className="text-gray-500 text-xs">10 agencies × 15 min</p>
                  </div>
                  
                  <div className="bg-white rounded-lg p-3 border border-gray-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-5 h-5 bg-green-500 rounded-md flex items-center justify-center flex-shrink-0">
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <h4 className="font-bold text-gray-900 text-xs">With Metapic</h4>
                    </div>
                    <p className="text-green-600 text-xl font-bold mb-1">2 minutes</p>
                    <p className="text-gray-500 text-xs">One signature → All PDFs</p>
                  </div>
                </div>
                
                <h4 className="text-lg font-bold text-gray-900 mt-6 mb-4">How It Works:</h4>
                <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                  <div className="space-y-5">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm">
                        1
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-1">Save Profiles Once</h4>
                        <p className="text-gray-600 text-sm">Create reusable profiles for models, photographers, locations, witnesses, properties. Enter details once, use forever.</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm">
                        2
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-1">Select Multiple Agencies</h4>
                        <p className="text-gray-600 text-sm">Check all agencies you need: Adobe Stock, Shutterstock, Getty Images, iStock, Freepik, Dreamstime, 123RF, Depositphotos, and more.</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm">
                        3
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-1">Client Signs ONCE</h4>
                        <p className="text-gray-600 text-sm">Native digital signature with smooth canvas. Model/property owner signs one time on your phone.</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm">
                        4
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-1">Instant Batch Generation</h4>
                        <p className="text-gray-600 text-sm">Tap "Generate" → All agency-specific PDFs created automatically with correct formatting, checkboxes, and signature placement.</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3 pt-3 border-t border-gray-200">
                      <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-1">100% Private & Secure</h4>
                        <p className="text-gray-600 text-sm">All release data stays on your phone. Never uploaded to server. Your clients' personal information remains completely private.</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 text-sm mb-2">Comprehensive Data Collection</h4>
                  <ul className="text-xs text-blue-800 space-y-1">
                    <li className="flex items-start gap-1.5">
                      <svg className="w-3 h-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                      <span>Adult and minor model categories with parent/guardian support</span>
                    </li>
                    <li className="flex items-start gap-1.5">
                      <svg className="w-3 h-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                      <span>Ethnicity, gender identity, full addresses</span>
                    </li>
                    <li className="flex items-start gap-1.5">
                      <svg className="w-3 h-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                      <span>Property and location details</span>
                    </li>
                    <li className="flex items-start gap-1.5">
                      <svg className="w-3 h-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                      <span>Witness information for verification</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* AI Tools Hub */}
        <section className="max-w-7xl mx-auto px-4 py-20">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-bold mb-4">
              AI Tools Hub
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
              Complete AI Tools Suite for Stock Creators
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From prompt generation to bulk image creation, everything you need to produce high-quality stock content
            </p>
          </div>

          {/* AI Tools Grid */}
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* 1. Prompt Generator */}
            <div className="bg-white rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all border border-gray-100">
              <div className="mb-6 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl h-48 flex items-center justify-center">
                <div className="text-center p-4">
                  <div className="w-16 h-16 mx-auto mb-3 bg-blue-200 rounded-xl flex items-center justify-center">
                    <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </div>
                  <p className="text-sm text-gray-600 font-medium">Prompt Generator</p>
                </div>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-3">AI Prompt Generator</h4>
              <p className="text-gray-600 mb-4 leading-relaxed">
                Generate creative, AI-powered prompts tailored for stock photography. Save favorites to collections and browse curated libraries.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Load prompts directly into Image Generator</span>
                </li>
                <li className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Save to collections for later use</span>
                </li>
                <li className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Browse curated prompt libraries</span>
                </li>
              </ul>
            </div>

            {/* 2. AI Image Generator (Bulk) */}
            <div className="bg-white rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all border border-gray-100">
              <div className="mb-6 bg-gradient-to-br from-pink-50 to-rose-50 rounded-xl h-48 flex items-center justify-center">
                <div className="text-center p-4">
                  <div className="w-16 h-16 mx-auto mb-3 bg-pink-200 rounded-xl flex items-center justify-center">
                    <svg className="w-8 h-8 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <p className="text-sm text-gray-600 font-medium">Image Generator</p>
                </div>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-3">Bulk AI Image Generator</h4>
              <p className="text-gray-600 mb-4 leading-relaxed">
                Produce high-quality stock images in bulk using FLUX 1.1 Pro, FLUX Realism LoRA, and Recraft V3 with SVG support.
              </p>
              <ul className="space-y-2 text-sm text-gray-600 mb-4">
                <li className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-pink-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Batch generate multiple images per prompt</span>
                </li>
                <li className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-pink-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Auto-save to device gallery</span>
                </li>
                <li className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-pink-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Manage collections with gallery preview</span>
                </li>
              </ul>

            </div>

            {/* 3. Image Upscaler */}
            <div className="bg-white rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all border border-gray-100">
              <div className="mb-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl h-48 flex items-center justify-center">
                <div className="text-center p-4">
                  <div className="w-16 h-16 mx-auto mb-3 bg-green-200 rounded-xl flex items-center justify-center">
                    <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                    </svg>
                  </div>
                  <p className="text-sm text-gray-600 font-medium">Upscaler</p>
                </div>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-3">Image Upscaler</h4>
              <p className="text-gray-600 mb-4 leading-relaxed">
                Enhance image resolution up to 5x with batch upscaling support. Meet microstock size requirements easily.
              </p>
              <ul className="space-y-2 text-sm text-gray-600 mb-4">
                <li className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Scale factors: 2x, 4x, 5x</span>
                </li>
                <li className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Preview before/after results</span>
                </li>
                <li className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Chain with Image Generator workflow</span>
                </li>
              </ul>

            </div>

            {/* 4. Background Remover */}
            <div className="bg-white rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all border border-gray-100">
              <div className="mb-6 bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl h-48 flex items-center justify-center">
                <div className="text-center p-4">
                  <div className="w-16 h-16 mx-auto mb-3 bg-purple-200 rounded-xl flex items-center justify-center">
                    <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                    </svg>
                  </div>
                  <p className="text-sm text-gray-600 font-medium">Background Remover</p>
                </div>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-3">Background Remover</h4>
              <p className="text-gray-600 mb-4 leading-relaxed">
                Remove backgrounds instantly with high-quality PNG output featuring transparency. Perfect for product photos and portraits.
              </p>
              <ul className="space-y-2 text-sm text-gray-600 mb-4">
                <li className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Batch processing support</span>
                </li>
                <li className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Download individually or in bulk</span>
                </li>
                <li className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Chain with Upscaler or Image Generator</span>
                </li>
              </ul>

            </div>
          </div>
        </section>

        {/* Multi-FTP Upload Section */}
        <section className="bg-gradient-to-b from-green-50 to-white py-20">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
              <div className="space-y-6">
                <div className="inline-block px-4 py-2 bg-green-600 text-white rounded-full text-sm font-bold mb-2">
                  FTP Uploader
                </div>
                <h2 className="text-3xl md:text-5xl font-bold text-gray-900">
                  Multi FTP upload to unlimited FTP connections!
                </h2>
                <p className="text-xl text-gray-600 leading-relaxed">
                  Upload simultaneously to Adobe Stock, Shutterstock, Getty Images, iStock, Pond5, Alamy, Freepik, Dreamstime, 123RF, Depositphotos, and more directly from your phone!
                </p>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                      <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Enterprise-Grade Reliability</h4>
                      <p className="text-gray-600">Robust retry logic, connection timeout handling, detailed progress tracking</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                      <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Secure Credential Management</h4>
                      <p className="text-gray-600">FTP credentials stored only on your device, never on our servers</p>
                    </div>
                  </div>
                  
                  
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                      <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Batch Upload</h4>
                      <p className="text-gray-600">Upload hundreds of files to multiple servers with one tap</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                      <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Custom FTP</h4>
                      <p className="text-gray-600">Use it like filezilla on your phone, upload any file to any FTP server</p>
                    </div>
                  </div>
                </div>
              </div>
              
              
              <img 
                src="/images/mobile-app/Mobile-phone-FTP-microstock.jpeg" 
                alt="Multi-FTP Upload to Stock Agencies" 
                className="max-w-sm mx-auto shadow-2xl"
              />
            </div>
          </div>
        </section>

        {/* Goal Setting & Income Planning */}
        <section className="max-w-7xl mx-auto px-4 py-20">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <img 
              src="/images/mobile-app/set-goal-microstock.jpeg" 
              alt="Set Your Goal and Income Planning" 
              className="max-w-sm mx-auto shadow-2xl"
            />
            
            <div className="space-y-6">
              <div className="inline-block px-4 py-2 bg-amber-100 text-amber-700 rounded-full text-sm font-bold mb-2">
                Gamification
              </div>
              <h2 className="text-3xl md:text-5xl font-bold text-gray-900">
                Turn Stock Photography into Predictable Income
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                Set monthly income targets, track daily progress, and stay motivated with gamification features.
              </p>
              
              <div className="space-y-4">
                <div className="bg-white rounded-xl shadow-md p-5 border border-gray-100">
                  <h4 className="font-bold text-gray-900 mb-2">Auto-Calculated Insights</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Total files needed to reach goal</li>
                    <li>• Daily upload quotas</li>
                    <li>• Content breakdowns by type</li>
                    <li>• Estimated earnings timeline</li>
                  </ul>
                </div>
                
                <div className="bg-white rounded-xl shadow-md p-5 border border-gray-100">
                  <h4 className="font-bold text-gray-900 mb-2">Stay Motivated</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Daily, weekly, monthly progress tracking</li>
                    <li>• Streak counters and tier badges</li>
                    <li>• Milestone notifications</li>
                    <li>• Visual progress indicators</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* What to Shoot - AI Moodboard Maker */}
        <section className="bg-gradient-to-b from-pink-50 to-purple-50 py-20">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-full text-sm font-bold mb-4">
                Content Inspiration
              </div>
              <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
                What to Shoot — AI Moodboard Maker
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Never run out of photo ideas. AI suggests keywords, instant visual inspiration, and save screenshots to your personal moodboard.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12 items-start max-w-6xl mx-auto mb-12">
              <div className="space-y-6">
                <div className="bg-gradient-to-br from-pink-500 to-purple-600 rounded-2xl p-8 text-white shadow-2xl">
                  <h3 className="text-2xl font-bold mb-4">3-Step Creative Workflow</h3>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-white text-purple-600 rounded-full flex items-center justify-center flex-shrink-0 font-black text-lg">
                        1
                      </div>
                      <div>
                        <h4 className="font-bold mb-1">AI Keyword Generator</h4>
                        <p className="text-sm opacity-90">Type any category (e.g., "cozy workspace") → AI generates 20-40 keyword ideas instantly</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-white text-purple-600 rounded-full flex items-center justify-center flex-shrink-0 font-black text-lg">
                        2
                      </div>
                      <div>
                        <h4 className="font-bold mb-1">Visual Inspiration Browser</h4>
                        <p className="text-sm opacity-90">Tap keyword → Browse reference images from Yandex/Google Images directly in-app</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-white text-purple-600 rounded-full flex items-center justify-center flex-shrink-0 font-black text-lg">
                        3
                      </div>
                      <div>
                        <h4 className="font-bold mb-1">Moodboard Screenshot Saver</h4>
                        <p className="text-sm opacity-90">Save inspiring images to organized moodboards. Add notes, view fullscreen, create collections.</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
                  <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <svg className="w-5 h-5 text-pink-600" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    Why Photographers Love This
                  </h4>
                  <ul className="text-sm text-gray-600 space-y-2">
                    <li className="flex items-start gap-2">
                      <svg className="w-4 h-4 flex-shrink-0 mt-0.5 text-green-600" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                      <span><strong>100% Offline-First:</strong> Local storage, no backend required</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <svg className="w-4 h-4 flex-shrink-0 mt-0.5 text-green-600" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                      <span><strong>AI-Powered:</strong> Uses Nova Micro for smart keyword generation</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <svg className="w-4 h-4 flex-shrink-0 mt-0.5 text-green-600" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                      <span><strong>Organized Collections:</strong> Moodboards grouped by keyword for easy reference</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <svg className="w-4 h-4 flex-shrink-0 mt-0.5 text-green-600" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                      <span><strong>Free Input:</strong> Type any category or use suggested templates</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <svg className="w-4 h-4 flex-shrink-0 mt-0.5 text-green-600" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                      <span><strong>Instant Visual Reference:</strong> Browse examples without leaving the app</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
                  <h4 className="font-bold text-gray-900 mb-3">Use Cases</h4>
                  <ul className="text-sm text-gray-600 space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600 font-bold">→</span>
                      <span>Planning next photoshoot themes</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600 font-bold">→</span>
                      <span>Finding composition inspiration</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600 font-bold">→</span>
                      <span>Discovering trending visual styles</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600 font-bold">→</span>
                      <span>Organizing creative references</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600 font-bold">→</span>
                      <span>Beating creative block</span>
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="space-y-6">
                <div className="rounded-2xl aspect-[9/16] max-w-sm mx-auto shadow-2xl overflow-hidden bg-black">
                  <iframe
                    className="w-full h-full"
                    src="https://www.youtube.com/embed/6fE7Ny301v8"
                    title="What to Shoot - AI Moodboard Maker Demo"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Who Is Metapic For Section */}
        <section className="max-w-7xl mx-auto px-4 py-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
              Who Is Metapic For?
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100 hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Beginner Stock Photographers</h3>
              <p className="text-gray-600">Looking for an easy entry into microstock with guided workflows and tutorials</p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100 hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Mobile-Only Creators</h3>
              <p className="text-gray-600">Need a full-featured app to create and upload stock content on the go</p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100 hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">AI Image Creators</h3>
              <p className="text-gray-600">Want batch workflows, prompt generation, and bulk image creation</p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100 hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">High-Volume Contributors</h3>
              <p className="text-gray-600">Managing large uploads and metadata across multiple agencies</p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100 hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Studios & Teams</h3>
              <p className="text-gray-600">Coordinating multi-agency releases and FTP uploads efficiently</p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100 hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Professional Photographers</h3>
              <p className="text-gray-600">Need comprehensive release management and professional tools</p>
            </div>
          </div>
        </section>

        {/* FAQs Section */}
        <section className="bg-gray-50 py-20">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
              Frequently Asked Questions
            </h2>
            
            <div className="max-w-3xl mx-auto space-y-6">
              <div className="bg-white rounded-xl p-6 shadow-md">
                <h4 className="font-bold text-gray-900 mb-2 text-lg">
                  Is Metapic available for iOS?
                </h4>
                <p className="text-gray-600">
                  Currently, Metapic is available for Android. iOS version is coming soon. Download the Android version from Google Play Store today.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-md">
                <h4 className="font-bold text-gray-900 mb-2 text-lg">
                  How does the credit system work?
                </h4>
                <p className="text-gray-600">
                  Different features use different amounts of credits. Credits are used for AI-powered features like metadata generation, image upscaling, and image generation. Each feature has its own credit cost based on complexity.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-md">
                <h4 className="font-bold text-gray-900 mb-2 text-lg">
                  Are my FTP credentials safe?
                </h4>
                <p className="text-gray-600">
                  Yes. Your FTP credentials are stored only on your device and never sent to our servers. We take your security seriously and use industry-standard encryption for all data storage.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-md">
                <h4 className="font-bold text-gray-900 mb-2 text-lg">
                  Can I use Metapic offline?
                </h4>
                <p className="text-gray-600">
                  No, you will need an internet connection most of the time to connect to API data for AI-powered features like metadata generation, image upscaling, background removal, and FTP uploads.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-md">
                <h4 className="font-bold text-gray-900 mb-2 text-lg">
                  Which stock agencies are supported?
                </h4>
                <p className="text-gray-600">
                  Metapic supports ALL microstock agencies for metadata generation. For direct FTP upload, we support unlimited FTP connections including Adobe Stock, Shutterstock, Getty Images, iStock, Pond5, Alamy, Freepik, Dreamstime, 123RF, Depositphotos, and any agency that provides FTP access.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 relative overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute top-0 left-0 w-full h-full opacity-10">
            <div className="absolute top-10 left-10 w-64 h-64 bg-white rounded-full blur-3xl"></div>
            <div className="absolute bottom-10 right-10 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          </div>
          
          <div className="max-w-7xl mx-auto px-4 text-center relative z-10">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Start Selling Stock Smarter Today
            </h2>
            <p className="text-xl md:text-2xl text-white opacity-90 mb-4 max-w-3xl mx-auto">
              Transform your stock photography workflow with the ultimate all-in-one mobile app
            </p>
            <p className="text-lg text-indigo-100 mb-10 max-w-2xl mx-auto">
              Join 1,250+ photographers already using Metapic to boost productivity and maximize earnings
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
              <button 
                onClick={handleDownloadApp}
                className="inline-flex items-center gap-3 px-8 py-4 bg-black hover:bg-gray-900 text-white rounded-xl font-medium transition-all shadow-2xl hover:shadow-3xl transform hover:scale-105"
              >
                <svg className="w-10 h-10" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                </svg>
                <div className="text-left">
                  <div className="text-xs opacity-90">GET IT ON</div>
                  <div className="text-xl font-bold -mt-1">Google Play</div>
                </div>
              </button>
            </div>
            
            <div className="flex items-center justify-center gap-8 text-white text-sm">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Free to Start</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>No Credit Card Required</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>iOS Coming Soon</span>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}
